import type { CSSProperties, JSX } from 'react';
import type { HamburgerMenuItem } from './HamburgerMenu';

// ---------------------------------------------------------------------------
// 区切り線 / 常設項目ボタン
// ---------------------------------------------------------------------------

/** メニュー内のセクション区切り線。 */
export function MenuSeparator(): JSX.Element {
  return <div className="hamburger-menu__separator" role="separator" />;
}

export interface SimpleItemProps {
  /** 行頭の絵文字アイコン。 */
  readonly icon: string;
  /** ラベル。 */
  readonly label: string;
  /** クリックハンドラ。 */
  readonly onClick: () => void;
}

/**
 * アイコン + ラベルだけの単純なメニュー項目（テーマ / アイコン設定 / 規約類）。
 * `extraItems` 用の {@link ItemRow} と違い variant / PRO バッジは持たない。
 */
export function SimpleItem({ icon, label, onClick }: SimpleItemProps): JSX.Element {
  return (
    <button
      type="button"
      className="hamburger-menu__item"
      role="menuitem"
      onClick={onClick}
    >
      <span aria-hidden="true" className="hamburger-menu__item-icon">
        {icon}
      </span>
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// 内部: メニュー1行 (extraItems / footerItems 用)
// ---------------------------------------------------------------------------

interface ItemRowProps {
  readonly item: HamburgerMenuItem;
  readonly onClickWrapped: () => void;
}

/**
 * `extraItems` の1行を描画するコンポーネント。separator / variant /
 * PRO バッジを 1 箇所に集約する。CSS クラスは既存の `hamburger-menu__item`
 * を共用しつつ、variant 用の色だけインラインで上書きする。
 */
export function ItemRow({ item, onClickWrapped }: ItemRowProps): JSX.Element {
  const variantStyle: CSSProperties =
    item.variant === 'danger' ? { color: 'var(--error)' } : {};

  return (
    <>
      {item.separatorBefore && <div style={separatorStyle} role="separator" />}
      <button
        type="button"
        className="hamburger-menu__item"
        role="menuitem"
        onClick={onClickWrapped}
        title={item.title}
        style={variantStyle}
      >
        <span aria-hidden="true" className="hamburger-menu__item-icon">
          {item.icon}
        </span>
        <span style={{ flex: 1 }}>{item.label}</span>
        {item.premium && (
          <span
            style={proBadgeStyle}
            aria-label="PRO限定機能"
            title="アップグレードユーザー向けの機能（PRO）"
          >
            PRO
          </span>
        )}
      </button>
    </>
  );
}

// ---------------------------------------------------------------------------
// インラインスタイル
// ---------------------------------------------------------------------------

const separatorStyle: CSSProperties = {
  height: '1px',
  background: 'var(--line-soft)',
  margin: '0.375rem 0',
};

/**
 * PRO バッジ。将来 paywall が有効化されたとき、free ユーザーには
 * 「アップグレードで利用可能」と読める色合いにしてある（黄〜橙系）。
 */
const proBadgeStyle: CSSProperties = {
  display: 'inline-block',
  background: 'linear-gradient(135deg, #F4B400 0%, #FF8800 100%)',
  color: '#2C1F18',
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  padding: '2px 6px',
  borderRadius: '3px',
  marginLeft: '4px',
};
